import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";

interface Notice { heading: string; body: string; button_label: string; button_url: string }

/** Personal card set by staff for one member — shows once on their next visit until dismissed. */
export function PersonalNotice() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [hidden, setHidden] = useState(false);
  const { data } = useQuery<{ notice: Notice | null }>({ queryKey: ["my-notice"], queryFn: () => api.get("/me/notice").then((r) => r.data), enabled: !!user, staleTime: 600000 });
  const n = data?.notice;
  if (!user || !n || hidden) return null;
  const dismiss = () => { setHidden(true); api.post("/me/notice/dismiss").then(() => qc.invalidateQueries({ queryKey: ["my-notice"] })).catch(() => undefined); };
  const external = /^https?:\/\//.test(n.button_url);
  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] p-4 sm:inset-auto sm:bottom-6 sm:right-6 sm:w-[400px]" data-testid="personal-notice">
      <div className="relative border border-border bg-[var(--paper)] p-6 shadow-[0_18px_50px_rgba(0,0,0,.18)]" style={{ borderTop: "4px solid var(--wine)" }}>
        <button type="button" onClick={dismiss} className="absolute right-3 top-3 text-neutral-400 hover:text-[var(--wine)]" aria-label="Close" data-testid="personal-notice-close"><X className="h-4 w-4" /></button>
        <div className="font-brand text-[11px] font-bold uppercase tracking-[0.2em] text-[var(--wine)]">A note for you</div>
        <h3 className="mt-2 font-serif text-xl leading-snug" data-testid="personal-notice-heading">{n.heading}</h3>
        {n.body && <p className="mt-2 text-sm leading-relaxed text-neutral-700" data-testid="personal-notice-body">{n.body}</p>}
        {n.button_url && (external
          ? <a href={n.button_url} target="_blank" rel="noreferrer" onClick={dismiss} className="mt-4 inline-block border border-[var(--wine)] bg-[var(--wine)] px-4 py-2 text-xs uppercase tracking-widest text-[var(--paper)]" data-testid="personal-notice-button">{n.button_label || "Take a look"}</a>
          : <Button asChild className="mt-4 rounded-none" onClick={dismiss}><Link to={n.button_url} data-testid="personal-notice-button">{n.button_label || "Take a look"}</Link></Button>)}
      </div>
    </div>
  );
}
